import { useState, useEffect } from 'react';

/**
 * Hook to detect user's motion preferences and device capabilities
 * Returns optimized animation configurations for better performance
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isLowEndDevice, setIsLowEndDevice] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    // Detect low-end devices based on hardware concurrency and memory
    const detectLowEndDevice = () => {
      const hardwareConcurrency = navigator.hardwareConcurrency || 2;
      const deviceMemory = (navigator as any).deviceMemory || 4;
      const isSlowDevice = hardwareConcurrency <= 2 || deviceMemory <= 2;
      setIsLowEndDevice(isSlowDevice);
    };

    detectLowEndDevice();

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Simple animation configuration
  const getAnimationConfig = () => {
    if (prefersReducedMotion || isLowEndDevice) {
      return { duration: 0 };
    }
    return { duration: 0.3, ease: 'easeInOut' };
  };

  return {
    prefersReducedMotion,
    isLowEndDevice,
    shouldReduceMotion: prefersReducedMotion || isLowEndDevice,
    animationConfig: getAnimationConfig(),
    // Standardized snappy transition for drawers
    drawerTransition: (prefersReducedMotion || isLowEndDevice)
      ? { duration: 0 }
      : { type: "spring" as const, damping: 30, stiffness: 450, mass: 0.8 }
  };
};

export default useReducedMotion;